import CohortMultiSelect, {
  resolveCohortIds,
} from '@/components/cohorts/cohort-multi-select';
import { useCohorts } from '@/hooks/queries/cohorts';
import { useUpdateUserCohorts } from '@/hooks/queries/users';
import { getUserVariableStatuses, User } from '@/types/user';
import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Selection,
} from '@nextui-org/react';
import { useEffect, useMemo, useState } from 'react';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  cohortIds?: number[];
  onCohortsSaved?: (userId: number, cohortIds: number[]) => void;
}

export default function UserModal({
  isOpen,
  onClose,
  user,
  cohortIds = [],
  onCohortsSaved,
}: UserModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedCohortIds, setSelectedCohortIds] = useState<Selection>(
    new Set([])
  );
  const [showCohortError, setShowCohortError] = useState(false);

  const { data: cohorts } = useCohorts();

  const selectedCohortIdList = useMemo(
    () => resolveCohortIds(selectedCohortIds, cohorts?.results ?? []),
    [selectedCohortIds, cohorts?.results]
  );

  const hasSelectedCohorts = selectedCohortIdList.length > 0;

  const variableStatuses = useMemo(
    () => getUserVariableStatuses(user?.variables),
    [user?.variables]
  );

  const { mutate: saveUserCohorts, isPending } = useUpdateUserCohorts({
    onSuccess: () => {
      if (user?.id) {
        onCohortsSaved?.(user.id, selectedCohortIdList);
      }
      onClose();
    },
    successMessage: 'User updated successfully',
  });

  useEffect(() => {
    if (!isOpen || !user) return;

    setFirstName(user.first_name ?? '');
    setLastName(user.last_name ?? '');
    setEmail(user.email ?? '');
    setSelectedCohortIds(
      new Set(cohortIds.map((cohortId) => cohortId.toString()))
    );
    setShowCohortError(false);
  }, [isOpen, user, cohortIds]);

  const handleSave = () => {
    if (!user) return;

    setShowCohortError(true);

    if (!hasSelectedCohorts) {
      return;
    }

    saveUserCohorts({
      userId: user.id,
      data: {
        cohorts: selectedCohortIdList,
      },
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalContent>
        <ModalHeader>Edit User</ModalHeader>
        <ModalBody className="gap-4">
          <Input label="First Name" value={firstName} isDisabled isRequired />
          <Input label="Last Name" value={lastName} isDisabled isRequired />
          <Input label="Email" value={email} isDisabled isRequired />
          <CohortMultiSelect
            selectedKeys={selectedCohortIds}
            onSelectionChange={setSelectedCohortIds}
            isRequired
            isInvalid={showCohortError && !hasSelectedCohorts}
            errorMessage={
              showCohortError && !hasSelectedCohorts
                ? 'At least one cohort is required'
                : undefined
            }
          />
          {variableStatuses.length > 0 && (
            <div className="rounded-lg border border-default-200 p-4">
              <p className="text-sm font-medium text-default-700">
                Profile variables
              </p>
              <p className="mt-1 text-xs text-default-500">
                Completion status only — values are not shown here.
              </p>
              <ul className="mt-3 space-y-2 max-h-[200px] overflow-y-auto">
                {variableStatuses.map(({ name, filled }) => (
                  <li
                    key={name}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <span className="min-w-0 break-all font-mono text-default-700">
                      {name}
                    </span>
                    <Chip
                      size="sm"
                      variant="flat"
                      color={filled ? 'success' : 'default'}
                      className="shrink-0"
                    >
                      {filled ? 'Filled' : 'Not filled'}
                    </Chip>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="justify-between border-t mt-4">
          <Button variant="bordered" onPress={onClose}>
            Cancel
          </Button>
          <Button color="primary" onPress={handleSave} isLoading={isPending}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
